import type { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { websiteRequest } from 'src/editor/shared/utils/request'
import type { WebsitePagePreviewInterface } from 'src/shared/types/page-interface'
import type { WebsitePageResource } from 'src/shared/types/website-page-interface'
import type {
  WebsiteStylesInterface,
  WebsiteThemeForSaveInterface,
  WebsiteThemeInterface,
} from 'src/shared/types/website-settings-types'
import { pageEditorHttpClient } from '../shared/utils/page-editor-http-client'

export interface UpdateWebsiteDataInterface {
  name?: string
  defaultLocale?: PageLocaleEnum
  style?: WebsiteStylesInterface
  theme?: WebsiteThemeForSaveInterface
}

interface WebsiteDataResponseInterface extends WebsitePageResource {
  theme: WebsiteThemeInterface
  style: WebsiteStylesInterface
}

export const updateWebsite = (id: number, data: UpdateWebsiteDataInterface) =>
  websiteRequest<WebsiteDataResponseInterface>(`websites/${id}/edit`, 'PUT', {
    data,
  })

export interface FetchWebsitePreviewInterface {
  websiteId: number
  websiteLayoutId: number
  websitePageId: number
  pagePreviewData: WebsitePagePreviewInterface
  websiteSettings?: {
    style?: WebsiteStylesInterface
    theme?: WebsiteThemeInterface
  }
}

export interface FetchWebsitePreviewResponseInterface {
  previewHtml: string
}

export const fetchWebsitePreview = async ({
  websiteId,
  websiteLayoutId,
  websitePageId,
  pagePreviewData,
  websiteSettings,
}: FetchWebsitePreviewInterface) =>
  pageEditorHttpClient<FetchWebsitePreviewResponseInterface>(
    `page/${websiteId}/${websiteLayoutId}/${websitePageId}/preview`,
    'POST',
    {
      //@ts-expect-error fetcher uses response interface for payload
      data: { pagePreviewData, websiteSettings },
    },
  )

interface UpdateWebsiteResourceInterface {
  id: number
  domainId?: number
  name: string
  homePagePath?: string
  locale: PageLocaleEnum
  locales: PageLocaleEnum[]
}

interface WebsiteResourceResponseInterface {
  id: number
  name: string
  locale: PageLocaleEnum
  locales: PageLocaleEnum[]
  active: boolean
  domainId: number
  createdAt: string
}

export const updateWebsiteResource = ({
  id,
  domainId,
  name,
  homePagePath,
  locale,
  locales,
}: UpdateWebsiteResourceInterface) =>
  websiteRequest<WebsiteResourceResponseInterface>(`websites/${id}`, 'PUT', {
    data: {
      domainId,
      name,
      homePagePath,
      locale,
      locales,
    },
  })

interface AddWebsiteLocaleDataInterface {
  id: number
  locale: PageLocaleEnum
}

interface AddWebsiteLocaleResponseInterface {
  locale: PageLocaleEnum
  layoutPageContentId: number
}

export const addWebsiteLocale = ({
  id,
  locale,
}: AddWebsiteLocaleDataInterface) =>
  websiteRequest<AddWebsiteLocaleResponseInterface>(
    `websites/${id}/add-locale`,
    'POST',
    {
      data: {
        locale,
      },
    },
  )

interface RemoveWebsiteLocaleDataInterface {
  id: number
  locale: PageLocaleEnum
}

export const removeWebsiteLocale = ({
  id,
  locale,
}: RemoveWebsiteLocaleDataInterface) =>
  websiteRequest<void>(`websites/${id}/remove-locale`, 'POST', {
    data: {
      locale,
    },
  })

interface CopyWebsitePagesDataInterface {
  id: number
  locale: PageLocaleEnum
}

export const copyWebsitePages = ({
  id,
  locale,
}: CopyWebsitePagesDataInterface) =>
  websiteRequest<void>(`websites/${id}/copy-pages`, 'POST', {
    data: {
      locale,
    },
  })
